/**
 * PRODUCT_DEV-596: iOS Safari 対応のクリップボード書き込みヘルパー。
 *
 * iOS Safari/WebKit は clipboard 書き込みを「ユーザー操作(タップ)と同期した処理」でしか
 * 許可しない。タップ後に await(fetch / LLM 要約 / SHA-256 等)を挟むと transient activation が
 * 失効し、`writeText` が NotAllowedError で reject する(Android/PC の Chrome/Firefox は緩いため
 * 成功していた)。栃銀テナントのコピーエラーの根本原因。
 *
 * 対策: `ClipboardItem` に本文 Promise を渡し、書き込み自体はクリック同期内に開始する。
 * Safari はこの Promise の解決を待ってクリップボードへ反映するため、本文組み立てに数秒かかる
 * 非同期処理があってもジェスチャが失効しない。ClipboardItem 非対応環境(一部 Firefox 等)は
 * 従来どおり Promise を await → `writeText` にフォールバックする。
 *
 * ⚠ 呼び出しは必ずユーザー操作ハンドラ内で「他の await より前に」行うこと。
 *    本文を組み立てる非同期処理は textPromise の中に閉じ込め、この関数へ渡す。
 */
export async function writeClipboardFromPromise(
  textPromise: Promise<string>,
): Promise<void> {
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    // クリック同期内に write を開始し、本文 Blob は Promise で遅延解決する。
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": textPromise.then(
          (text) => new Blob([text], { type: "text/plain" }),
        ),
      }),
    ]);
    return;
  }
  // フォールバック(ClipboardItem 非対応): 本文確定後に writeText。
  const text = await textPromise;
  await navigator.clipboard.writeText(text);
}
